import React, { useContext, useMemo } from 'react';
import { Button, Modal, Row, Form, Col } from 'react-bootstrap';
import { Row as RRow, Column } from 'react-table';
import { Loading, RoleUtils } from '@galaxy/lorule-react-commons';
import { useMsal } from '@azure/msal-react';
import NinModal from '../../common/modal/NinModal';
import ConfigContext from '../../../contexts/ConfigContext';
import { getProjectInvoice } from '../../../api/projectBalanceApi';
import { InvoiceHeader } from '../../../api/projectBalanceApiTypes';
import useAsyncWithState from '../../../hooks/useAsyncWithState';
import { formatCurrency, formatDate } from '../../../utils/displayUtils';
import DataTable from '../../common/datatables/DataTable';
import { generateProjectInvoiceDistributionsSubRow } from './ProjectInvoiceDistributionsSubRow';

export interface ProjectInvoiceModalProps {
    amId: number;
    amNumber: number;
    invoiceId: number;
    commitment: boolean;
    toggleButton: JSX.Element;
}

const ProjectInvoiceContent = (props: {
    amId: number;
    amNumber: number;
    invoiceId: number;
    commitment: boolean;
    toggleModal: () => void;
}) => {
    const { instance } = useMsal();
    const config = useContext(ConfigContext);
    const roles = RoleUtils.getRoles(instance);

    const { amId, amNumber, invoiceId, commitment } = props;

    const { value: invoiceHeader } = useAsyncWithState(
        () =>
            getProjectInvoice(
                config,
                amId,
                invoiceId,
                commitment,
                !RoleUtils.checkRole(roles, ['ActionMemo.Admin']),
            ),
        null as InvoiceHeader | null,
    );

    const columns = React.useMemo(
        () => [
            {
                id: 'menu',
                Header: '',
                accessor: 'menu',
                Cell: ({ row }: { row: RRow }) =>
                    useMemo(
                        () => (
                            <span {...row.getToggleRowExpandedProps()}>
                                {row.isExpanded ? (
                                    <i className="fas fa-minus-square" />
                                ) : (
                                    <i className="fas fa-plus-square" />
                                )}
                            </span>
                        ),
                        [],
                    ),
                disableSortBy: true,
                width: 50,
            },
            {
                Header: 'Line #',
                accessor: 'lineNumber',
                width: 75,
            },
            {
                Header: 'Description',
                accessor: 'description',
            },
            {
                Header: 'PO #',
                accessor: 'poNumber',
                width: 100,
            },
            {
                Header: 'Unit Price',
                accessor: 'unitPrice',
                width: 100,
                Cell: (cellProps: { value: number }) => formatCurrency(cellProps.value, 2),
            },
            {
                Header: 'Quantity',
                accessor: 'quantity',
                width: 100,
                Cell: (cellProps: { value: number }) => cellProps.value,
            },
            {
                Header: 'Amount',
                accessor: 'amount',
                width: 100,
                Cell: (cellProps: { value: number }) => formatCurrency(cellProps.value, 2),
            },
        ],
        [],
    );

    const renderRowSubComponent = generateProjectInvoiceDistributionsSubRow(
        amId,
        amNumber,
        invoiceId,
        commitment,
    );

    return invoiceHeader ? (
        <>
            <Row>
                <Col>
                    <Form.Group as={Row}>
                        <Form.Label column sm={6}>
                            Invoice Number
                        </Form.Label>
                        <Col sm={6} className="mt-2">
                            {invoiceHeader.invoiceNumber}
                        </Col>
                    </Form.Group>
                    <Form.Group as={Row}>
                        <Form.Label column sm={6}>
                            Invoice Date
                        </Form.Label>
                        <Col sm={6} className="mt-2">
                            {formatDate(invoiceHeader.invoiceDate)}
                        </Col>
                    </Form.Group>
                    <Form.Group as={Row}>
                        <Form.Label column sm={6}>
                            Currency
                        </Form.Label>
                        <Col sm={6} className="mt-2">
                            {invoiceHeader.currencyCode}
                        </Col>
                    </Form.Group>
                </Col>
                <Col>
                    <Form.Group as={Row}>
                        <Form.Label column sm={6}>
                            Subtotal
                        </Form.Label>
                        <Col sm={6} className="mt-2 text-right">
                            {formatCurrency(invoiceHeader.discountAmount, 2)}
                        </Col>
                    </Form.Group>
                    <Form.Group as={Row}>
                        <Form.Label column sm={6}>
                            Tax
                        </Form.Label>
                        <Col sm={6} className="mt-2 text-right">
                            {formatCurrency(invoiceHeader.taxAmount, 2)}
                        </Col>
                    </Form.Group>
                    <Form.Group as={Row}>
                        <Form.Label column sm={6}>
                            Amount
                        </Form.Label>
                        <Col sm={6} className="mt-2 text-right">
                            {formatCurrency(invoiceHeader.invoiceAmount, 2)}
                        </Col>
                    </Form.Group>
                </Col>
            </Row>

            {invoiceHeader.lines && invoiceHeader.lines.length > 0 && (
                <div className="react-table">
                    <h5 className="border-top pt-3 mb-2">Invoice Lines</h5>
                    <DataTable
                        columns={columns as Column<Object>[]}
                        data={invoiceHeader.lines}
                        noGlobalFilter
                        noPagination
                        initialState={{ pageSize: 1000 }}
                        renderRowSubComponent={renderRowSubComponent}
                    />
                </div>
            )}
        </>
    ) : (
        <Loading />
    );
};

const ProjectInvoiceModal = (props: ProjectInvoiceModalProps) => (
    <NinModal
        size="xl"
        button={props.toggleButton}
        render={(toggleModal) => (
            <>
                <Modal.Header closeButton>
                    <Modal.Title>Invoice Details</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <ProjectInvoiceContent {...props} toggleModal={toggleModal} />
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="button"
                        className="btn-outline-secondary"
                        onClick={toggleModal}
                    >
                        Close
                    </Button>
                </Modal.Footer>
            </>
        )}
    />
);

export default ProjectInvoiceModal;
